import React, { useState, useEffect } from 'react';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { useAppTheme } from '../../context/ThemeContext';
import { Card, Button, Badge, Table, Modal, Alert } from '../../components/ui';
import { formatDate } from '../../utils/date';
import { useNavigate } from 'react-router-dom';

interface DonationRequest {
  id: string;
  charity_id: string;
  charity_name: string;
  product_id: string;
  product_name: string;
  quantity: number;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  created_at: string;
  updated_at: string;
}

type TableColumn<T> = {
  header: string;
  accessor: keyof T | ((item: T) => React.ReactNode);
  className?: string;
};

const DonationRequests: React.FC = () => {
  const { user } = useAuthV2();
 useNavigate();
  useAppTheme();
  const [requests, setRequests] = useState<DonationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<DonationRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<'accept' | 'reject' | 'cancel'>('accept');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      // Import createClient to ensure we get the authenticated client
      const { createClient } = await import('../../services/supabase');
      const supabase = await createClient();
      
      // Ensure we have a user ID before querying
      if (!user?.id) {
        console.error('User ID not available for donation requests query');
        // Initialize with empty requests instead of showing an error
        // This prevents redirection to WaitingApproval
        setRequests([]);
        setLoading(false);
        return;
      }
      
      const { data, error } = await supabase
        .from('donation_requests')
        .select(`
          *,
          charities:charity_id (name),
          products:product_id (name)
        `)
        .eq('business_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Define a type for the donation request from Supabase
      type DonationRequestData = {
        id: string;
        charity_id: string;
        charities: { name: string };
        product_id: string;
        products: { name: string };
        quantity: number;
        status: string;
        created_at: string;
        updated_at: string;
      };
      
      const formattedRequests = data.map((request: DonationRequestData) => ({
        id: request.id,
        charity_id: request.charity_id,
        charity_name: request.charities.name,
        product_id: request.product_id,
        product_name: request.products.name,
        quantity: request.quantity,
        status: request.status as 'pending' | 'accepted' | 'rejected' | 'cancelled',
        created_at: request.created_at,
        updated_at: request.updated_at
      }));

      setRequests(formattedRequests);
    } catch (err) {
      setError('Failed to fetch donation requests');
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (request: DonationRequest, action: 'accept' | 'reject' | 'cancel') => {
    setSelectedRequest(request);
    setModalAction(action);
    setIsModalOpen(true);
  };

  const confirmAction = async () => {
    if (!selectedRequest) return;

    try {
      // Import createClient to ensure we get the authenticated client
      const { createClient } = await import('../../services/supabase');
      const supabase = await createClient();
      
      const { error } = await supabase
        .from('donation_requests')
        .update({ status: modalAction === 'accept' ? 'accepted' : modalAction === 'reject' ? 'rejected' : 'cancelled' })
        .eq('id', selectedRequest.id);

      if (error) throw error;

      await fetchRequests();
      setIsModalOpen(false);
      setSelectedRequest(null);
    } catch (err) {
      setError('Failed to update request status');
      console.error('Error updating request:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'warning', text: 'Pending' },
      accepted: { color: 'success', text: 'Accepted' },
      rejected: { color: 'error', text: 'Rejected' },
      cancelled: { color: 'gray', text: 'Cancelled' }
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    return <Badge color={config.color as any}>{config.text}</Badge>;
  };

  const columns: TableColumn<DonationRequest>[] = [
    { header: 'Charity', accessor: 'charity_name' },
    { header: 'Product', accessor: 'product_name' },
    { header: 'Quantity', accessor: 'quantity' },
    { header: 'Status', accessor: (request: DonationRequest) => getStatusBadge(request.status) },
    { header: 'Requested', accessor: (request: DonationRequest) => formatDate(request.created_at) },
    {
      header: 'Actions',
      accessor: (request: DonationRequest) => (
        <div className="space-x-2">
          {request.status === 'pending' && (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleAction(request, 'accept')}
              >
                Accept
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleAction(request, 'reject')}
              >
                Reject
              </Button>
            </>
          )}
          {request.status === 'pending' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleAction(request, 'cancel')}
            >
              Cancel
            </Button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <h1 className="text-2xl font-medium">Donation Requests</h1>
      </div>
      
      {error && (
        <Alert type="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Card>
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-navy-blue"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mb-4">
              <svg className="h-16 w-16 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Donation Requests</h3>
            <p className="text-gray-500">There are currently no donation requests to display.</p>
          </div>
        ) : (
          <Table
            columns={columns}
            data={requests}
            isLoading={false}
            emptyMessage="No donation requests found"
          />
        )}
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`${modalAction.charAt(0).toUpperCase() + modalAction.slice(1)} Request`}
      >
        <div className="space-y-4">
          <p>
            Are you sure you want to {modalAction} this donation request from{' '}
            {selectedRequest?.charity_name} for {selectedRequest?.quantity} units of{' '}
            {selectedRequest?.product_name}?
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant={modalAction === 'accept' ? 'primary' : 'danger'}
              onClick={confirmAction}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DonationRequests; 